import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, HelpCircle, ArrowRight } from "lucide-react";

interface ArticleCardProps {
  title: string;
  excerpt: string;
  category: string;
  categoryColor?: string;
  readTime: number;
  href: string;
}

export function ArticleCard({
  title,
  excerpt,
  category,
  categoryColor = "bg-blue-100 text-blue-700",
  readTime,
  href,
}: ArticleCardProps) {
  return (
    <Link to={href} className="group block">
      <motion.article
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.4 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:border-blue-200 hover:shadow-md"
      >
        <div className="flex items-start gap-5">
          <div
            className={`h-12 w-12 ${categoryColor} flex flex-shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-110`}
          >
            <HelpCircle className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                {category}
              </span>
              <span className="text-slate-300">•</span>
              <span className="flex items-center gap-1 text-xs font-medium text-slate-500">
                <Clock className="h-3 w-3" />
                {readTime} min read
              </span>
            </div>
            <h3 className="mb-2 line-clamp-1 text-xl font-bold text-slate-900 transition-colors group-hover:text-blue-600">
              {title}
            </h3>
            <p className="line-clamp-2 leading-relaxed text-slate-600">{excerpt}</p>
          </div>
          <div className="translate-x-2 transform self-center opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
            <ArrowRight className="h-5 w-5 text-blue-600" />
          </div>
        </div>
      </motion.article>
    </Link>
  );
}
